import React from "react";
import { Link } from "react-router-dom";
import { GraduationCap, CheckCircle2, CalendarClock } from "lucide-react";
import { SectionHeading, PageHeader } from "@/components/public/ui";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const ELIGIBILITY = [
  "Current Pack Brothers Hospitality employee (or dependent), in good standing",
  "Enrolled or accepted in an accredited college, university or trade program",
  "Demonstrated commitment to community and hospitality",
];
const REQUIREMENTS = [
  "Completed application form",
  "Short personal statement",
  "Proof of enrollment or acceptance",
  "One letter of recommendation",
];
const DATES = [
  { label: "Applications Open", value: "January 15" },
  { label: "Submission Deadline", value: "April 30" },
  { label: "Recipients Announced", value: "June" },
];
const FAQ = [
  { q: "Who can apply?", a: "The scholarship supports Pack Brothers team members and their dependents pursuing higher education or a trade program." },
  { q: "How much is the award?", a: "Award amounts vary each year based on available funding and the number of recipients selected." },
  { q: "Can I apply more than once?", a: "Yes. Eligible applicants may reapply in subsequent years." },
  { q: "How are recipients selected?", a: "A committee reviews applications based on eligibility, the personal statement, and community involvement." },
];

export default function Scholarships() {
  return (
    <div className="pt-20">
      <PageHeader icon={GraduationCap} eyebrow="Scholarship Program" title="Investing in Our People"
        intro="The Pack Brothers Scholarship supports the education and ambitions of our team members and their families." />

      <section className="mx-auto max-w-[1000px] px-5 py-20 lg:px-8">
        <div className="grid gap-14 lg:grid-cols-2">
          <div>
            <SectionHeading eyebrow="Overview" title="A Commitment to Growth" />
            <p className="mt-5 text-[16px] leading-relaxed text-[rgba(48,56,60,0.8)]">
              Hospitality has always been about people. Through our scholarship program, we invest in the education and future of the team members who make Pack Brothers Hospitality special — and in the families who support them.
            </p>
            <h3 className="mt-10 font-serif text-2xl text-[#18344A]">Eligibility</h3>
            <ul className="mt-4 space-y-2.5">
              {ELIGIBILITY.map((x) => (
                <li key={x} className="flex items-start gap-2.5 text-sm text-[rgba(48,56,60,0.8)]"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#526E7E]" /> {x}</li>
              ))}
            </ul>
            <h3 className="mt-8 font-serif text-2xl text-[#18344A]">Application Requirements</h3>
            <ul className="mt-4 space-y-2.5">
              {REQUIREMENTS.map((x) => (
                <li key={x} className="flex items-start gap-2.5 text-sm text-[rgba(48,56,60,0.8)]"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#526E7E]" /> {x}</li>
              ))}
            </ul>
          </div>

          <div>
            <div className="rounded-2xl border border-[var(--pb-border)] bg-[#F5F3EF] p-6">
              <h3 className="flex items-center gap-2 font-serif text-2xl text-[#18344A]"><CalendarClock className="h-5 w-5 text-[#526E7E]" /> Important Dates</h3>
              <div className="mt-4 divide-y divide-[var(--pb-border)]">
                {DATES.map((d) => (
                  <div key={d.label} className="flex items-center justify-between py-3 text-sm">
                    <span className="text-[rgba(48,56,60,0.7)]">{d.label}</span>
                    <span className="font-semibold text-[#18344A]">{d.value}</span>
                  </div>
                ))}
              </div>
              <Link to="/contact" data-testid="scholarship-apply" className="mt-6 flex items-center justify-center rounded-full bg-[#18344A] px-6 py-3 text-sm font-semibold uppercase tracking-[0.06em] text-white hover:bg-[#18344A]/90">
                Start an Application
              </Link>
            </div>

            <h3 className="mt-10 font-serif text-2xl text-[#18344A]">Frequently Asked</h3>
            <Accordion type="single" collapsible className="mt-3">
              {FAQ.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-sm font-medium text-[#18344A]">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-sm text-[rgba(48,56,60,0.72)]">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </div>
  );
}
